/** importing dependencies **/
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";

/** importing libraries **/
import { IAuthResponse } from "../../interfaces/interfaces";
import AuthUtils from "../../utils/auth.utils";
import { RootState } from "../store";
import { userApi } from "../../services/apis/user.api";
import { configEnv } from "../../services/constant";

export type ISigninDetails = {
    email: string;
    password: string;
};

export type ISignupDetails = {
    email: string;
    name: string;
    password: string;
    role: string[];
    jobTitle?: string;
    dob: string;
    subject: string;
    organization: string;
    department?: string;
    unit?: string;
};

export interface IAuthState {
    access_token: string | undefined | null;
    user: IApiUser | undefined | null;
    isAuthenticated: boolean;
    adminMode: boolean;
    ownerMode: boolean;
    superAdminMode: boolean;
    p3mSuperAdminMode: boolean;
}

export interface IApiUser {
    _id: string;
    name: string;
    email: string;
    jobTitle?: string;
    isAdmin: boolean;
    isOwner: boolean;
    adminFeaturesAllowed: [];
    licenseAccepted: boolean;
    googleRefreshToken: string;
    googleAccessToken: string;
    googleCalendarId: string;
    license: {
        _id: string;
        licenseId: string;
        organizationId: string;
        description: string;
        subscriptionType: string;
        isActive: boolean;
        paymentStatus: string;
        startDate: string;
        endDate: string;
        createdAt: string;
        updatedAt: string;
        __v: number;
    };
}

const initialState: IAuthState = {
    access_token: AuthUtils.getLocalStorage("access_token"),
    user: null,
    isAuthenticated: false,
    adminMode: false,
    ownerMode: false,
    superAdminMode: false,
    p3mSuperAdminMode: false,
};

/**
 * @description - To load user using token in local storage on page refresh or fresh page visit
 */
const loaduser = createAsyncThunk(
    "auth/loaduser",
    async (id: any, { dispatch, getState }) => {
        try {
            const { access_token } = (getState() as RootState).auth;

            const header = {
                Authorization: `Bearer ${access_token}`,
            };

            const data = await userApi.getAuthdetails(id, header);
            if (!data?.data || !data?.data?.token) {
                return;
            }
            dispatch(success(data));
        } catch {
            // Error toast surfaced by the global axios interceptor.
        }
    }
);

/**
 * @description - To register a new user
 */

const register = createAsyncThunk(
    "auth/register",
    async (details: ISignupDetails, { dispatch }) => {
        try {
            const data = await userApi.register(details);

            if (!data?.data) {
                return;
            }
            dispatch(registerSuccess(data));
            return data;
        } catch {
            // Error toast surfaced by the global axios interceptor.
        }
    }
);

const ownerRegister = createAsyncThunk(
    "auth/owner-register",
    async (details: ISignupDetails, { dispatch }) => {
        try {
            const data = await userApi.ownerRegister(details);

            if (!data?.data) {
                return;
            }
            dispatch(registerSuccess(data));
            return data;
        } catch {
            // Error toast surfaced by the global axios interceptor.
        }
    }
);

/**
 * @description - To login user
 */
const login = createAsyncThunk(
    "auth/login",
    async (details: ISigninDetails, { dispatch, rejectWithValue }) => {
        try {
            const data = await userApi.login(details);

            if (!data?.data || !data?.data?.token) {
                return rejectWithValue(data);
            }
            dispatch(success(data));
            return data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data || err);
        }
    }
);

const ownerLogin = createAsyncThunk(
    "auth/ownerlogin",
    async (details: ISigninDetails, { dispatch, getState }) => {
        try {
            const data = await userApi.ownerLogin(details, {
                Authorization: `Bearer ${(getState() as RootState).auth.access_token}`,
            });

            if (!data?.data || !data?.data?.token) {
                return;
            }
            dispatch(success(data));
            return data;
        } catch {
            // Error toast surfaced by the global axios interceptor.
        }
    }
);

/**
 * @description - To get user profile
 */
const profile = createAsyncThunk(
    "auth/profile",
    async (args: { id: any; name: any }, { rejectWithValue, getState }) => {
        try {
            const { access_token } = (getState() as RootState).auth;

            const header = {
                Authorization: `Bearer ${access_token}`,
            };
            const data = await userApi.getProfile(args.id, args.name, header);
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data || error);
        }
    }
);

const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        /**
         * Logout user
         */
        logout: (state) => {
            AuthUtils.removeLocalStorage("access_token");
            AuthUtils.clearAuthToken();

            // Clean up all invitation-related data for anonymous users
            localStorage.removeItem("invitation_token");
            localStorage.removeItem("anonymous_invitation_token");
            sessionStorage.removeItem("invitationData");

            // Clear recent search history scoped to this user (and anon)
            try {
                Object.keys(localStorage)
                    .filter((k) => k.startsWith("p3m:recentSearches:"))
                    .forEach((k) => localStorage.removeItem(k));
            } catch {
                /* ignore */
            }

            state.access_token = null;
            state.user = null;
            state.isAuthenticated = false;
            state.adminMode = false;
            state.ownerMode = false;
            state.superAdminMode = false;
            state.p3mSuperAdminMode = false;

            Sentry.setUser(null);
        },

        /**
         * Handle Auth Error
         */
        error: (state) => {
            AuthUtils.removeLocalStorage("access_token");
            AuthUtils.setAuthToken();

            Object.assign(state, initialState, { access_token: null });

            Sentry.setUser(null);
        },

        /**
         * Handle Auth Request Success
         */
        success: (state, { payload }: { payload: { data: IAuthResponse } }) => {
            AuthUtils.setLocalStorage("access_token", payload.data.token);
            AuthUtils.setAuthToken();

            state.access_token = payload.data.token;
            state.isAuthenticated = true;
            state.user = payload.data as any;
            state.adminMode = payload.data.role.includes("Admin");
            state.ownerMode = payload.data.role.includes("Owner");
            state.superAdminMode = payload.data.role.includes("SuperAdmin");
            state.p3mSuperAdminMode = payload.data.role.includes("P3MSuperAdmin");

            // Identify the session in Sentry (id/role/org only — no name or
            // email) so issues show affected-user counts and replays are
            // searchable by user and organization.
            const u = payload.data as any;
            Sentry.setUser({
                id: u._id || u.id || undefined,
                role: Array.isArray(u.role) ? u.role.join(",") : u.role,
                organizationId:
                    u.organization?._id || u.organization || undefined,
            });
        },
        /**
         * Handle Auth Register Success
         */
        registerSuccess: (state, { payload }: { payload: { data: any } }) => {
            state.access_token = null;
            state.user = payload.data;
            state.isAuthenticated = false;
        },

        /**
         * Update profile success
         */
        updateProfileSuccess(
            state,
            {
                payload,
            }: { payload: { name?: string; email?: string; password?: string } }
        ) {
            if (state.user) {
                Object.assign(state.user, payload);
            }
        },

        updateAuthState: (
            state,
            { payload }: { payload: Partial<IAuthState> }
        ) => {
            Object.assign(state, payload);
        },

        updateLicenseInfo: (
            state,
            {
                payload,
            }: { payload: { licenseAccepted: boolean; licenseId: any } }
        ) => {
            if (state.user) {
                state.user.licenseAccepted = payload.licenseAccepted;
                state.user.license = payload.licenseId;
            }
        },
    },
});

const {
    error,
    logout,
    success,
    registerSuccess,
    updateProfileSuccess,
    updateAuthState,
    updateLicenseInfo,
} = authSlice.actions;

export function getAuthState(state: RootState): IAuthState {
    return state.auth;
}

export {
    error,
    login,
    ownerLogin,
    loaduser,
    logout,
    register,
    ownerRegister,
    success,
    updateAuthState,
    updateProfileSuccess,
    profile,
    updateLicenseInfo,
};

export const authReducer = authSlice.reducer;
